'use client';

import { createTheme, responsiveFontSizes } from '@mui/material';
import { Arguments } from 'yargs';
import { enableColorInversion, invertedColorsClass, SKIP_INVERTED_COLORS_ATTR } from './colorInversion';
import { colors } from './colors';
import { addDefaultProps } from './defaultProps';
import { addMixins } from './mixins';
import { darkPalette, lightPalette } from './palette';
import { styleOverrides } from './styleOverrides';
import { themeBreakpoints, themeShadows, themeShape, themeSpacing, themeZIndex } from './themeConfig';
import type {} from './types';
import { typography } from './typography';

export { addDefaultProps, addMixins, colors, enableColorInversion, invertedColorsClass, SKIP_INVERTED_COLORS_ATTR };
export { themeVars, type MdsThemeVars } from './themeVars';

/**
 * Internal theme options for advanced use cases, e.g. overriding the color scheme
 * selector
 */
export const themeOptions = {
  colorSchemes: {
    light: {
      palette: lightPalette,
    },
    dark: {
      palette: darkPalette,
    },
  },
  typography,
  // Shadows are disabled in MDS
  shadows: themeShadows,
  breakpoints: themeBreakpoints,
  shape: themeShape,
  spacing: themeSpacing,
  components: styleOverrides,
  cssVariables: {
    cssVarPrefix: 'mds',
    // Allow switching color schemes manually
    colorSchemeSelector: 'data-color-scheme',
  },
  zIndex: themeZIndex,
} satisfies Arguments<typeof createTheme>[0];

/**
 * Decorate theme created with createTheme() to enable color inversion, add default
 * component props, theme mixins, etc.
 */
export const decorateTheme = (theme: ReturnType<typeof createTheme>) =>
  addDefaultProps(enableColorInversion(responsiveFontSizes(addMixins(theme))));

/**
 * Marex Design System theme for Material UI
 *
 * To disable either dark or light mode, see [MDS docs](http://mds.marex.com)
 */
export const theme = decorateTheme(createTheme(themeOptions));
